"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import {
  uploadCreativeTemplateAsset,
  generateCreativeTemplate,
  getCreativeTemplateSignedUrl,
} from "@/app/_api/creativeTemplates";
import { HttpError } from "@utilities/api";
import { getMediaDuration } from "@utilities/videoUtils";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";
import { appUrls } from "@/constants";
import { USER_INPUT_PLACEHOLDER } from "../../constants";
import type { ContentWorkflowStep, GenerationModel, WorkflowInputConfig } from "../types";

function buildRedirectUrl(redirectUrlTemplate: string, assetId?: string, userInput?: string): string {
  const url = new URL(redirectUrlTemplate);
  if (assetId) url.searchParams.set("asset_id", assetId);
  const prompt = url.searchParams.get("prompt");
  if (prompt && userInput) {
    url.searchParams.set("prompt", prompt.replaceAll(USER_INPUT_PLACEHOLDER, userInput));
  }
  return url.toString();
}

function buildClaimUrl({
  assetId,
  modelId,
  refinedPrompt,
}: {
  assetId: string;
  modelId?: string | null;
  refinedPrompt?: string | null;
}): string {
  const url = new URL(`${appUrls.imageVideo}/history`);
  url.searchParams.set("claim_asset_id", assetId);
  if (modelId) url.searchParams.set("model", modelId);
  if (refinedPrompt) url.searchParams.set("prompt", refinedPrompt);
  return url.toString();
}

export interface UseContentWorkflowReturn {
  step: ContentWorkflowStep;
  userInput: string | null;
  resultUrl: string | null;
  redirectCtaUrl: string | null;
  inputPreviewUrl: string | null;
  selectedFile: File | null;
  error: string | null;
  handleFileSelected: (file: File) => void;
  handleTextSubmit: (text: string) => void;
  handleSubmit: (userInput?: string) => void;
  handleRemoveFile: () => void;
  restart: () => void;
}

export function useCreativeTemplate({
  templateSlug,
  templateCategory,
  variantSlug,
  generationModel,
  config,
  redirectUrlTemplate,
  promptRequiresInput = false,
}: {
  templateSlug: string;
  templateCategory: string;
  variantSlug: string;
  generationModel: GenerationModel;
  config: WorkflowInputConfig;
  redirectUrlTemplate: string;
  promptRequiresInput?: boolean;
}): UseContentWorkflowReturn {
  const [step, setStep] = useState<ContentWorkflowStep>("idle");
  const [userInput, setUserInput] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [redirectCtaUrl, setRedirectCtaUrl] = useState<string | null>(null);
  const [inputPreviewUrl, setInputPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { error, setError } = useAutoDismissError();

  const t = useTranslations("ClientKeys.creative_templates");
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputPreviewUrlRef = useRef<string | null>(null);
  const generatingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (generatingTimerRef.current) {
      clearTimeout(generatingTimerRef.current);
      generatingTimerRef.current = null;
    }
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const executeWorkflow = useCallback(
    async ({ file, prompt }: { file?: File; prompt?: string }) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      let assetId: string | undefined;
      setUserInput(prompt ?? null);

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
        template_category: templateCategory,
        variant_slug: variantSlug,
        input_type: file ? "file" : "text",
        generation_model: generationModel,
      });

      try {
        // Upload step (for file inputs)
        if (file) {
          setStep("uploading");
          const uploadResult = await uploadCreativeTemplateAsset(file, abortController.signal);
          assetId = uploadResult.asset_id;

          if (abortController.signal.aborted) return;
        }

        // Video templates require an account — skip the generate call entirely
        // to avoid counting against the global daily rate limit for image generation.
        if (config.resultType === "video") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
            template_category: templateCategory,
            variant_slug: variantSlug,
            error_type: "rate_limited",
          });
          setRedirectCtaUrl(buildRedirectUrl(redirectUrlTemplate, assetId, prompt));
          setStep("rate_limited");
          return;
        }

        // Delay showing "generating" to avoid a flash when the API responds instantly (e.g. rate limit)
        generatingTimerRef.current = setTimeout(() => setStep("generating"), 500);

        const generateResult = await generateCreativeTemplate({
          templateSlug,
          assetId,
          userInput: prompt,
          signal: abortController.signal,
        }).finally(() => {
          clearTimeout(generatingTimerRef.current!);
          generatingTimerRef.current = null;
        });

        if (abortController.signal.aborted) return;

        const resultAssetId = generateResult.asset_id;
        if (!resultAssetId) {
          throw new Error("No asset ID returned from generation");
        }

        const { url } = await getCreativeTemplateSignedUrl(resultAssetId, abortController.signal);

        if (abortController.signal.aborted) return;

        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
          template_category: templateCategory,
          variant_slug: variantSlug,
          input_type: file ? "file" : "text",
          generation_model: generationModel,
        });

        setRedirectCtaUrl(
          buildClaimUrl({
            assetId: resultAssetId,
            modelId: generateResult.model_id,
            refinedPrompt: generateResult.refined_prompt ?? prompt,
          }),
        );
        setResultUrl(url);
        setStep("result");
      } catch (err) {
        if (abortController.signal.aborted) return;

        const errorType = err instanceof HttpError ? err.errorType : null;

        switch (errorType) {
          case "image_too_large":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t("error_image_too_large"));
            setStep("file_selected");
            break;

          case "generation_refused":
          case "cms_unreachable":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t(errorType === "generation_refused" ? "error_generation_refused" : "error_cms_unreachable"));
            setStep(file ? "file_selected" : "idle");
            break;

          case "quota_exceeded":
          case "rate_limited": {
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });

            const refinedPrompt =
              err instanceof HttpError ? (err.data?.refined_prompt as string | undefined) : undefined;
            const redirectUrl = new URL(buildRedirectUrl(redirectUrlTemplate, assetId, prompt));
            if (refinedPrompt) redirectUrl.searchParams.set("prompt", refinedPrompt);
            setRedirectCtaUrl(redirectUrl.toString());

            setStep(errorType === "rate_limited" ? "rate_limited" : "quota_exceeded");
            break;
          }

          default:
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_message: err instanceof Error ? err.message : "Unknown error",
              generation_model: generationModel,
            });
            setError(t("error_generic"));
            setStep("idle");
        }
      }
    },
    [templateSlug, templateCategory, variantSlug, generationModel, redirectUrlTemplate, setError, t],
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      // Validate file size
      if (config.maxFileSizeMb > 0 && file.size > config.maxFileSizeMb * 1024 * 1024) {
        setError(t("error_file_too_large", { maxSize: config.maxFileSizeMb }));
        return;
      }

      // Validate video duration
      if (config.maxVideoDurationSec > 0 && file.type.startsWith("video/")) {
        try {
          const duration = await getMediaDuration(file);
          if (duration > config.maxVideoDurationSec) {
            setError(t("error_video_too_long", { maxDuration: config.maxVideoDurationSec }));
            return;
          }
        } catch {
          // If duration can't be determined, allow the upload to proceed
        }
      }

      setError(null);

      // Create preview URL
      if (inputPreviewUrlRef.current) {
        URL.revokeObjectURL(inputPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      inputPreviewUrlRef.current = previewUrl;
      setInputPreviewUrl(previewUrl);

      setSelectedFile(file);

      if (promptRequiresInput) {
        setStep("file_selected");
      } else {
        executeWorkflow({ file });
      }
    },
    [config.maxFileSizeMb, config.maxVideoDurationSec, promptRequiresInput, executeWorkflow, setError, t],
  );

  const handleSubmit = useCallback(
    (userInput?: string) => {
      if (selectedFile) {
        executeWorkflow({ file: selectedFile, prompt: userInput });
      }
    },
    [selectedFile, executeWorkflow],
  );

  const handleRemoveFile = useCallback(() => {
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
    setSelectedFile(null);
    setInputPreviewUrl(null);
    setStep("idle");
    setError(null);
  }, [setError]);

  const handleTextSubmit = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      setError(null);
      executeWorkflow({ prompt: text.trim() });
    },
    [executeWorkflow, setError],
  );

  const restart = useCallback(() => {
    cleanup();
    setStep("idle");
    setResultUrl(null);
    setRedirectCtaUrl(null);
    setInputPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
  }, [cleanup, setError]);

  return {
    step,
    userInput,
    resultUrl,
    redirectCtaUrl,
    inputPreviewUrl,
    selectedFile,
    error,
    handleFileSelected,
    handleTextSubmit,
    handleSubmit,
    handleRemoveFile,
    restart,
  };
}
